import { ArrowClockwiseIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import {
  invalidateGetRepositoryThreatModelRefreshRequests,
  useGetRepositoryThreatModelRefreshRequests,
} from '../../utils/api-client/api-hooks'
import type { RefreshRequestResponse } from '../../utils/api-client/api-schemas.ts'
import { RefreshRequestDrawer } from './refresh-request-drawer.tsx'
import { RefreshRequestTable } from './refresh-request-table.tsx'
import * as styles from './update-history-view.css.ts'

type UpdateHistoryViewProps = {
  repositoryId: string
}

export const UpdateHistoryView: React.FC<UpdateHistoryViewProps> = ({ repositoryId }) => {
  const queryClient = useQueryClient()
  const [selectedRequestId, setSelectedRequestId] = useState<string | undefined>(undefined)

  const { data, isLoading, isFetching } = useGetRepositoryThreatModelRefreshRequests({ repositoryId })

  // The API omits superseded rows from the default projection; filter defensively in case any leak
  // through, and to keep the invariant local to the UI.
  const visibleRequests = useMemo<RefreshRequestResponse[]>(
    () => (data?.items ?? []).filter(request => request.state !== 'superseded'),
    [data]
  )

  const selectedRequest = useMemo(
    () => (selectedRequestId ? visibleRequests.find(request => request.id === selectedRequestId) : undefined),
    [selectedRequestId, visibleRequests]
  )

  const handleSelectRequest = useCallback((requestId: string) => {
    setSelectedRequestId(requestId)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setSelectedRequestId(undefined)
  }, [])

  // "Reload", not "Refresh": this re-fetches the list, and on a tab whose rows are *refresh requests* and
  // whose statuses read "Regenerating", a Refresh button reads as a request to regenerate the model.
  const handleReload = useCallback(() => {
    invalidateGetRepositoryThreatModelRefreshRequests(queryClient, { repositoryId })
  }, [queryClient, repositoryId])

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <DefaultButton
          buttonType="secondary"
          size="small"
          state={isFetching ? 'loading' : 'default'}
          icon={<ArrowClockwiseIcon size={16} weight="regular" />}
          onClick={handleReload}
        >
          Reload
        </DefaultButton>
      </div>

      <RefreshRequestTable
        requests={visibleRequests}
        isLoading={isLoading}
        selectedRequestId={selectedRequestId}
        onSelectRequest={handleSelectRequest}
      />

      {selectedRequest && (
        <RefreshRequestDrawer
          repositoryId={repositoryId}
          initialRequest={selectedRequest}
          onClose={handleCloseDrawer}
        />
      )}
    </div>
  )
}
